import type { Feature, MultiPolygon, Polygon } from 'geojson'
import type { LngLatCoord } from '@gpx-spa/geo-domain'
import { bufferLineStringMeters } from './buffer-line-string-meters.js'
import { simplifyLineStringCoordinates } from './simplify-line-string-coordinates.js'

export type LineBufferGeometryCache = {
  clear: () => void
  getSimplified: (pathId: string, raw: LngLatCoord[], qualityPercent: number) => LngLatCoord[]
  getBufferedPolygon: (
    pathId: string,
    coords: LngLatCoord[],
    radiusMeters: number,
    qualityPercent: number
  ) => Feature<Polygon | MultiPolygon> | null
  /**
   * Fills the memo maps after the same geometry was computed off-thread (Web Worker),
   * so {@link getSimplified} / {@link getBufferedPolygon} stay consistent for Device API JSON etc.
   */
  primeCorridorResult: (
    pathId: string,
    radiusMeters: number,
    qualityPercent: number,
    simplifiedCoords: LngLatCoord[],
    buffer: Feature<Polygon | MultiPolygon> | null
  ) => void
  /** True if {@link getBufferedPolygon} would return a memoized polygon for this key (no Turf work). */
  hasCorridorBufferCached: (pathId: string, radiusMeters: number, qualityPercent: number) => boolean
}

/**
 * Memoizes simplified polylines and per-path buffered polygon features.
 */
export function createLineBufferGeometryCache(): LineBufferGeometryCache {
  const simplified = new Map<string, LngLatCoord[]>()
  const buffered = new Map<string, Feature<Polygon | MultiPolygon>>()

  function clear() {
    simplified.clear()
    buffered.clear()
  }

  function sk(pathId: string, q: number) {
    return `${pathId}|q${Math.round(q)}`
  }

  function bk(pathId: string, radiusM: number, q: number) {
    return `${pathId}|b${Math.round(radiusM)}|q${Math.round(q)}`
  }

  function getSimplified(pathId: string, raw: LngLatCoord[], qualityPercent: number): LngLatCoord[] {
    if (qualityPercent >= 100) {
      return raw
    }
    const key = sk(pathId, qualityPercent)
    const hit = simplified.get(key)
    if (hit) return hit
    const next = simplifyLineStringCoordinates(raw as [number, number][], qualityPercent) as LngLatCoord[]
    simplified.set(key, next)
    return next
  }

  function getBufferedPolygon(
    pathId: string,
    coords: LngLatCoord[],
    radiusMeters: number,
    qualityPercent: number
  ): Feature<Polygon | MultiPolygon> | null {
    const key = bk(pathId, radiusMeters, qualityPercent)
    const hit = buffered.get(key)
    if (hit) return hit
    const f = bufferLineStringMeters(coords, radiusMeters)
    if (!f?.geometry) return null
    buffered.set(key, f)
    return f
  }

  function primeCorridorResult(
    pathId: string,
    radiusMeters: number,
    qualityPercent: number,
    simplifiedCoords: LngLatCoord[],
    buffer: Feature<Polygon | MultiPolygon> | null
  ) {
    simplified.set(sk(pathId, qualityPercent), simplifiedCoords)
    if (buffer?.geometry) {
      buffered.set(bk(pathId, radiusMeters, qualityPercent), buffer)
    }
  }

  function hasCorridorBufferCached(
    pathId: string,
    radiusMeters: number,
    qualityPercent: number
  ): boolean {
    return buffered.has(bk(pathId, radiusMeters, qualityPercent))
  }

  return { clear, getSimplified, getBufferedPolygon, primeCorridorResult, hasCorridorBufferCached }
}
